import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Book, Facebook, Twitter, Mail, Heart, Send, MessageCircle } from 'lucide-react';
import { useDomainStore } from '../../hooks/useStore';
import { pageAPI } from '../../services/api';
import AdDisplay from '../AdDisplay/AdDisplay';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { domain } = useDomainStore();
  const [footerPages, setFooterPages] = useState({ support: [], legal: [], company: [] });

  useEffect(() => {
    fetchFooterPages();
  }, []);

  const fetchFooterPages = async () => {
    try {
      const response = await pageAPI.getFooterPages();
      setFooterPages(response.data || { support: [], legal: [], company: [] });
    } catch (error) {
      console.error('Failed to fetch footer pages:', error);
    }
  };

  const siteName = domain?.name || 'NovelVibe';
  const contact = domain?.contact || {};

  return (
    <footer className="footer">
      {/* Footer Ad - Above footer content */}
      <AdDisplay position="footer" className="footer-ad" />
      
      <div className="footer-container">
        <div className="footer-main">
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              {domain?.logo ? (
                <img src={domain.logo} alt={siteName} className="footer-logo-img" />
              ) : (
                <Book size={24} />
              )}
              <span>{siteName}</span>
            </Link>
            <p className="footer-desc">
              {domain?.description || 'The leading online novel reading platform. Read for free, anytime, anywhere.'}
            </p>
            <div className="footer-social">
              {contact.facebook && (
                <a href={contact.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <Facebook size={20} />
                </a>
              )}
              {contact.telegram && (
                <a href={contact.telegram} target="_blank" rel="noopener noreferrer" aria-label="Telegram">
                  <Send size={20} />
                </a>
              )}
              {contact.zalo && (
                <a href={contact.zalo} target="_blank" rel="noopener noreferrer" aria-label="Zalo">
                  <MessageCircle size={20} />
                </a>
              )}
              {contact.email && (
                <a href={`mailto:${contact.email}`} aria-label="Email">
                  <Mail size={20} />
                </a>
              )}
              {!contact.facebook && !contact.telegram && !contact.zalo && !contact.email && (
                <>
                  <button className="social-btn" aria-label="Facebook"><Facebook size={20} /></button>
                  <button className="social-btn" aria-label="Twitter"><Twitter size={20} /></button>
                  <button className="social-btn" aria-label="Email"><Mail size={20} /></button>
                </>
              )}
            </div>
          </div>

          <div className="footer-links">
            <div className="footer-column">
              <h4>Quick Links</h4>
              <Link to="/">Home</Link>
              <Link to="/genres">Genres</Link>
              <Link to="/novels?sort=popular">Hot Novels</Link>
              <Link to="/novels?sort=newest">Latest Updates</Link>
            </div>

            <div className="footer-column">
              <h4>Genres</h4>
              <Link to="/genre/cultivation">Cultivation</Link>
              <Link to="/genre/romance">Romance</Link>
              <Link to="/genre/martial-arts">Martial Arts</Link>
              <Link to="/genre/urban">Urban</Link>
            </div>

            <div className="footer-column">
              <h4>Support</h4>
              {footerPages.support.length > 0 ? (
                footerPages.support.map(page => (
                  <Link key={page._id} to={`/page/${page.slug}`}>{page.title}</Link>
                ))
              ) : (
                <>
                  <Link to="/page/about-us">About Us</Link>
                  <Link to="/page/contact">Contact</Link>
                </>
              )}
              {footerPages.legal.map(page => (
                <Link key={page._id} to={`/page/${page.slug}`}>{page.title}</Link>
              ))}
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>
            © {currentYear} {siteName}. Made with <Heart size={14} className="heart" /> for readers.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
